import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { submitSchedule, cancelCSVInput, processCSV, updateScheduleRows } from '../../actions/restaurantPoolSchedule'
import PropTypes from 'prop-types'
import './styles.css'
import RestaurantPoolScheduleCSVUpload from './RestaurantPoolScheduleCSVUpload'
import RestaurantPoolScheduleErrors from './RestaurantPoolScheduleErrors'
import RestaurantPoolScheduleConfirm from './RestaurantPoolScheduleConfirm'
import RestaurantPoolScheduleResult from './RestaurantPoolScheduleResult'
import { Switch, Route } from 'react-router-dom'
import { push } from 'react-router-redux'
import { getDefaultTimezone } from '../../actions/system'
import { canManageLegacyTakeawayPoolActions, isMagicEnabledFromSearch } from '../../helpers/tenant'

export class RestaurantPoolSchedule extends React.Component {

  constructor(props){
    super(props)
    this.csvUploadUrl = "/restaurant-pool-schedule/csv"
    this.errorsUrl = "/restaurant-pool-schedule/errors"
    this.confirmUrl = "/restaurant-pool-schedule/confirm"
    this.uploadResultUrl = "/restaurant-pool-schedule/upload-result"
  }

  componentDidMount() {
    this.determineLocationFromState()
  }

  determineLocationFromState(){
    if(!this.props.scheduleRows || this.props.scheduleRows.length === 0){
      // We do not have any data so go to the start of the flow
      this.goto(this.csvUploadUrl)
    }
    else if(this.props.csvParseErrors && this.props.csvParseErrors.length > 0){
      // we have issues parsing the CSV so stay on the CSV page
      this.goto(this.csvUploadUrl)
    }
    else if(this.props.invalidScheduleRows && this.props.invalidScheduleRows.length > 0){
      // we have invalid rows so display the errors
      this.goto(this.errorsUrl)
    }
    else {
      // everthing looks fine so go to the confimration page
      this.goto(this.confirmUrl)
    }
  }

  goto(url){
    if(url !== this.props.pathname){
      this.props.push({
        pathname: url,
        search: this.props.locationSearch
      })
    }
  }

  render() {
    const canManagePoolSchedule = canManageLegacyTakeawayPoolActions(this.props.tenant, this.props.isMagicEnabled)
    const disabledTitle = canManagePoolSchedule ? '' : 'Function disabled for legacy Takeaway countries.'

    return <Switch>
      <Route exact path={this.csvUploadUrl} render={() => (
        <RestaurantPoolScheduleCSVUpload 
          key={this.props.csvParseRaw}
          csv={this.props.csvParseRaw}
          csvParseErrors={this.props.csvParseErrors}
          isLoading={this.props.isLoading}
          isActionDisabled={!canManagePoolSchedule}
          disabledTitle={disabledTitle}
          onBack={()=>{
            this.props.cancelCSVInput()
            this.goto('/')
          }} 
          onProcess={(csvString)=>{
            this.props.processCSV(csvString).then(()=>this.determineLocationFromState())
          }}/>
      )}/>
      <Route exact path={this.errorsUrl} render={() => (
        <RestaurantPoolScheduleErrors 
          key={this.props.lastScheduleRowUpdate}
          invalidScheduleRows={this.props.invalidScheduleRows}
          isLoading={this.props.isLoading}
          isActionDisabled={!canManagePoolSchedule}
          disabledTitle={disabledTitle}
          defaultTimeZone={this.props.defaultTimezone}
          onBack={()=>{
            this.goto(this.csvUploadUrl)
          }} 
          onUpdateScheduleRows={(updatedScheduleRows)=>{
            this.props.updateScheduleRows(updatedScheduleRows).then(()=>this.determineLocationFromState())
          }}
          onIgnoreErrors={()=>{
            this.goto(this.confirmUrl)
          }}/>

      )} />
      <Route exact path={this.confirmUrl} render={() => (
        <RestaurantPoolScheduleConfirm
          key={this.props.lastScheduleRowUpdate}
          validScheduleRows={this.props.validScheduleRows}
          isLoading={this.props.isLoading}
          isActionDisabled={!canManagePoolSchedule}
          disabledTitle={disabledTitle}

          onBack={()=>{
            this.goto(this.props.invalidScheduleRows.length > 0 
              ? this.errorsUrl 
              : this.csvUploadUrl )
          }}
          onSubmit={(validScheduleRows)=>{
            this.goto(this.uploadResultUrl)
            this.props.submitSchedule(validScheduleRows)
          }} />
      )} />
      <Route exact path={this.uploadResultUrl} render={() => (
        <RestaurantPoolScheduleResult
          validScheduleRows={this.props.validScheduleRows}
          isLoading={this.props.isLoading}
          scheduleUploadError={this.props.scheduleUploadError}
          scheduleUploadResult={this.props.scheduleUploadResult}

          onServerError={()=>{
            this.goto(this.props.invalidScheduleRows.length > 0
              ? this.errorsUrl
              : this.csvUploadUrl )
          }}
        />
      )} />
    </Switch>
  }
}

RestaurantPoolSchedule.propTypes = {
  csvParseRaw: PropTypes.string,
  csvParseErrors: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  lastScheduleRowUpdate: PropTypes.instanceOf(Date),
  scheduleRows: PropTypes.array.isRequired,
  validScheduleRows: PropTypes.array.isRequired,
  invalidScheduleRows: PropTypes.array.isRequired,
  defaultTimezone: PropTypes.string,
  pathname: PropTypes.string,
  locationSearch: PropTypes.string,
  tenant: PropTypes.string,
  isMagicEnabled: PropTypes.bool,
  scheduleUploadError: PropTypes.string,
  scheduleUploadResult: PropTypes.array,

  cancelCSVInput: PropTypes.func.isRequired,
  processCSV: PropTypes.func.isRequired,
  updateScheduleRows: PropTypes.func.isRequired,
  submitSchedule: PropTypes.func.isRequired,
  push:  PropTypes.func.isRequired,
}

const mapStateToProps = (state, props) => {
  const locationSearch = (state.router && state.router.location && state.router.location.search) || ''

  return {
    ...props,
    csvParseRaw: state.restaurantPoolSchedule.csvParseRaw,
    csvParseErrors: state.restaurantPoolSchedule.csvParseErrors,
    isLoading: state.restaurantPoolSchedule.isLoading,
    lastScheduleRowUpdate: state.restaurantPoolSchedule.lastScheduleRowUpdate,
    scheduleRows: state.restaurantPoolSchedule.scheduleRows || [],
    invalidScheduleRows: state.restaurantPoolSchedule.invalidScheduleRows || [],
    validScheduleRows: state.restaurantPoolSchedule.validScheduleRows || [],
    defaultTimezone: getDefaultTimezone(state),
    pathname: props.location.pathname,
    locationSearch: locationSearch,
    tenant: state.system && state.system.tenant,
    isMagicEnabled: isMagicEnabledFromSearch(locationSearch),
    scheduleUploadError: state.restaurantPoolSchedule.scheduleUploadError,
    scheduleUploadResult: state.restaurantPoolSchedule.scheduleUploadResult || []
  }
}

const mapDispatchToProps = dispatch => bindActionCreators({
  cancelCSVInput,
  processCSV,
  updateScheduleRows,
  submitSchedule,
  push
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(RestaurantPoolSchedule)



// WEBPACK FOOTER //
// ./src/containers/restaurant-pool-schedule/index.js